import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import VideoCard from "@/components/feature/VideoCard";
import SeoMeta from "@/components/feature/SeoMeta";
import { categories, hotVideos, allVideos } from "@/mocks/videos";

const faqData = [
  {
    q: "国产吃瓜视频合集多久更新一次？",
    a: "我们的内容团队会全天候跟踪全网热点事件，热门事件发生后通常会在数小时内整理完成并更新到对应分类。每日固定更新至少一次，重大事件会优先加急上线。",
  },
  {
    q: "如何快速找到想看的热门吃瓜视频？",
    a: "你可以在首页查看「热门推荐」与「排行榜」，也可以使用顶部搜索框输入关键词，或通过导航栏的分类入口按明星八卦、综艺名场面、热搜盘点等分类浏览，快速定位到感兴趣的内容。",
  },
  {
    q: "视频分类都有哪些？",
    a: "目前平台覆盖明星八卦、综艺名场面、热搜盘点、红毯现场、音乐现场、圈内秘闻六大分类，聚合了吃瓜视频合集、精选与大全内容，满足不同方向的观看需求。",
  },
  {
    q: "视频画质如何？支持哪些设备观看？",
    a: "视频均提供高清清晰度，支持电脑、平板和手机等多端自适应播放，无论在桌面端还是移动端都能获得流畅的观看体验。",
  },
  {
    q: "内容是否完全免费观看？",
    a: "平台内容完全免费，无需注册即可直接观看全部吃瓜视频合集与精选内容。我们希望通过免费优质内容，成为你日常吃瓜的首选站点。",
  },
  {
    q: "如何参与投稿或内容合作？",
    a: "如果你有独家内容或合作意向，可以通过页脚的邮箱与我们联系。我们欢迎真实、有趣、符合平台定位的视频素材投稿，审核通过后将优先推荐。",
  },
];

const categoryIcons: Record<string, string> = {
  mingxing: "ri-user-star-line",
  zongyi: "ri-tv-line",
  resou: "ri-fire-line",
  hongtan: "ri-shirt-line",
  yinyue: "ri-music-2-line",
  quannei: "ri-movie-line",
};

export default function Home() {
  const top = [...allVideos].sort((a, b) => b.heat - a.heat).slice(0, 5);
  const [main, ...rest] = hotVideos;

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="bg-background-50 min-h-screen">
      <SeoMeta
        title="国产吃瓜视频 - 吃瓜视频合集/大全精选与热门排行推荐"
        description="国产吃瓜视频网，聚合全网热门吃瓜视频合集与精选内容，按事件热度实时整理，覆盖明星八卦、综艺名场面、热搜盘点等分类。想看国产吃瓜视频大全、排行或最新推荐，点开即可按分类浏览热门吃瓜视频合集、精选与推荐。"
        type="website"
        jsonLd={[
          faqLd,
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "热门吃瓜视频推荐",
            itemListElement: allVideos.slice(0, 6).map((v, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: v.title,
              url: `https://www.readdy.ai/video/${v.id}`,
            })),
          },
        ]}
      />
      <SiteHeader />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=stylized%20dramatic%20entertainment%20stage%20with%20silhouetted%20crowd%20watching%20spotlight%20performance%2C%20warm%20coral%20red%20and%20golden%20orange%20stage%20lights%20against%20deep%20dark%20background%2C%20energy%20and%20excitement%20of%20gossip%20and%20trending%20momentum%2C%20abstract%20artistic%20cinematic%20composition%2C%20no%20text%20no%20faces&width=1800&height=900&seq=gua-hero-01&orientation=landscape"
            alt="国产吃瓜视频热门现场"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-background-50"></div>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 md:px-6 pt-32 pb-24 md:pt-44 md:pb-32 text-center w-full">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50/10 border border-background-50/30 text-background-50 text-sm backdrop-blur">
            <i className="ri-fire-line text-accent-400"></i>
            全网热门吃瓜视频 · 实时更新
          </span>
          <h1 className="mt-6 font-heading text-4xl md:text-6xl font-black text-background-50 leading-tight">
            一次看全国产吃瓜视频
            <span className="block text-accent-400">合集 · 大全 · 精选</span>
          </h1>
          <p className="mt-6 mx-auto max-w-2xl text-background-50/90 text-base md:text-lg leading-relaxed">
            聚合全网热门吃瓜视频合集与精选内容，按事件热度实时整理。想看国产吃瓜视频大全、排行或最新推荐，点开即可按分类浏览，省去到处翻找的麻烦。
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/rank"
              className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-bold whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              查看热门排行
            </Link>
            <Link
              to={`/category/${categories[0].id}`}
              className="px-8 py-3 rounded-full bg-background-50/10 border border-background-50/40 text-background-50 font-bold whitespace-nowrap hover:bg-background-50/20 transition-colors"
            >
              浏览视频分类
            </Link>
          </div>
        </div>
      </section>

      {/* 分类导航 */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">
              按分类浏览<span className="text-primary-600">吃瓜视频</span>
            </h2>
            <p className="mt-2 text-sm text-foreground-600">六大分类，覆盖你想看的每一个方向</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/category/${c.id}`}
              className="group bg-background-50 border border-background-200 rounded-xl p-5 text-center hover:border-primary-300 hover:bg-primary-50/50 transition-colors"
            >
              <span className="mx-auto inline-flex w-14 h-14 items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                <i className={`${categoryIcons[c.id] || "ri-play-circle-line"} text-2xl`}></i>
              </span>
              <h3 className="mt-3 font-bold text-foreground-950">{c.name}</h3>
              <p className="mt-1 text-xs text-foreground-500 line-clamp-2">{c.desc}</p>
              <span className="mt-2 inline-block text-xs text-primary-600 font-semibold">
                {c.count} 个视频
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* 热门推荐 */}
      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">
                热门吃瓜视频<span className="text-primary-600">推荐</span>
              </h2>
              <p className="mt-2 text-sm text-foreground-600">按事件热度实时整理的热门精选</p>
            </div>
            <Link
              to="/rank"
              className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-600 font-semibold hover:text-primary-700"
            >
              更多热门 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Link
              to={`/video/${main.id}`}
              className="group relative rounded-2xl overflow-hidden min-h-[280px] md:min-h-[340px] flex items-end"
            >
              <img
                src={main.cover}
                alt={main.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent"></div>
              <div className="relative p-6 md:p-8">
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-bold">
                  <i className="ri-fire-fill"></i> 今日最热
                </span>
                <h3 className="mt-3 text-xl md:text-2xl font-black text-background-50 leading-snug group-hover:underline">
                  {main.title}
                </h3>
                <p className="mt-2 text-sm text-background-50/85 flex items-center gap-4">
                  <span>
                    <i className="ri-eye-line mr-1"></i>
                    {main.views}
                  </span>
                  <span>
                    <i className="ri-time-line mr-1"></i>
                    {main.duration}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-play-circle-line"></i> 立即观看
                  </span>
                </p>
              </div>
            </Link>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {rest.map((v, i) => (
                <VideoCard key={v.id} video={v} rank={i + 2} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 视频流 + 排行榜 */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-end justify-between mb-6">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">
                  最新吃瓜视频<span className="text-primary-600">合集</span>
                </h2>
                <p className="mt-2 text-sm text-foreground-600">新鲜出炉的精选内容</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {allVideos.slice(3).map((v) => (
                <VideoCard key={v.id} video={v} />
              ))}
            </div>
          </div>

          {/* 排行榜 */}
          <aside className="lg:col-span-1">
            <div className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6">
              <div className="flex items-center gap-2 mb-5">
                <span className="inline-flex w-9 h-9 items-center justify-center rounded-full bg-accent-500 text-background-50">
                  <i className="ri-trophy-line"></i>
                </span>
                <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950">热度排行榜</h2>
              </div>
              <ol className="space-y-3">
                {top.map((v, i) => (
                  <li key={v.id}>
                    <Link
                      to={`/video/${v.id}`}
                      className="flex items-start gap-3 group"
                    >
                      <span
                        className={`mt-0.5 inline-flex w-7 h-7 shrink-0 items-center justify-center rounded-lg text-sm font-black ${
                          i < 3 ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-900"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <div className="min-w-0">
                        <h3 className="text-sm font-bold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {v.title}
                        </h3>
                        <span className="mt-1 inline-flex items-center gap-1 text-xs text-foreground-500">
                          <i className="ri-eye-line"></i>
                          {v.views}
                          <span className="mx-1 text-foreground-300">·</span>
                          <i className="ri-flashlight-fill text-accent-500"></i>
                          热度 {v.heat}
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ol>
              <Link
                to="/rank"
                className="mt-5 w-full inline-flex items-center justify-center gap-1 py-2.5 rounded-full bg-background-100 text-primary-600 text-sm font-bold hover:bg-primary-100 transition-colors"
              >
                查看完整榜单 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* 品牌介绍 */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=stylized%20warm%20abstract%20stage%20curtain%20backdrop%20with%20golden%20and%20coral%20light%20bokeh%20and%20soft%20glow%2C%20moody%20cinematic%20entertainment%20atmosphere%2C%20artistic%20dreamy%20composition%2C%20no%20text&width=1800&height=800&seq=gua-banner-01&orientation=landscape"
            alt="国产吃瓜视频品牌背景"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-primary-950/70"></div>
        </div>
        <div className="relative mx-auto max-w-7xl px-4 md:px-6 py-16 md:py-24 text-center w-full">
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-bold">
            <i className="ri-fire-line"></i> 全网吃瓜第一站
          </span>
          <h2 className="mt-5 font-heading text-3xl md:text-4xl font-black text-background-50">
            每天都有新瓜，每天都有新发现
          </h2>
          <p className="mt-5 mx-auto max-w-2xl text-background-50/85 text-base leading-relaxed">
            我们用心整理每一个热点，把分散在全网的热门吃瓜视频合集、精选与大全内容汇聚到一起。无论是最新吃瓜视频排行还是经典名场面回顾，都能在这里一次看全。
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {[
              { n: "1200+", t: "精选视频" },
              { n: "6", t: "视频分类" },
              { n: "24h", t: "实时更新" },
              { n: "100%", t: "免费观看" },
            ].map((s) => (
              <div key={s.t} className="p-4 rounded-xl bg-background-50/10 border border-background-50/20">
                <p className="text-2xl md:text-3xl font-black text-accent-400">{s.n}</p>
                <p className="mt-1 text-sm text-background-50/80">{s.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-4 md:px-6 py-14 md:py-20">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">
            常见<span className="text-primary-600">问题</span>
          </h2>
          <p className="mt-2 text-sm text-foreground-600">关于吃瓜视频平台，你想了解的全在这里</p>
        </div>
        <div className="space-y-3">
          {faqData.map((f, i) => (
            <details key={i} className="group bg-background-50 border border-background-200 rounded-xl overflow-hidden">
              <summary className="flex items-center justify-between gap-3 cursor-pointer p-5 list-none hover:bg-background-100/60 transition-colors">
                <span className="font-bold text-foreground-900 text-sm md:text-base">{f.q}</span>
                <i className="ri-add-line text-primary-500 group-open:rotate-45 transition-transform"></i>
              </summary>
              <p className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}